import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Toolbar from "@mui/material/Toolbar";

// project-imports
import Drawer from "./Drawer";
import Header from "./Header";
import PageChrome from "./PageChrome";
import Loader from "components/Loader";
import GlobalSearch from "components/GlobalSearch";
import {
  GlobalSearchProvider,
  useGlobalSearch,
} from "contexts/GlobalSearchContext";
import { useSelector } from "react-redux";

import { DRAWER_SLOT_WIDTH, MenuOrientation, HEADER_HEIGHT } from "config";
import useConfig from "hooks/useConfig";
import { handlerDrawerOpen, useGetMenuMaster } from "api/menu";
import RouteGuard from "routes/RouteGuard";
import "./dashboardV2.css";
import "components/GlobalSearch/globalSearch.css";

function useIsV2Shell(pathname) {
  return (
    pathname === "/home" ||
    pathname === "/experts" ||
    pathname.startsWith("/experts/") ||
    pathname === "/institutions" ||
    pathname.startsWith("/institutions/") ||
    pathname === "/newsletter" ||
    pathname === "/contribuer" ||
    pathname.startsWith("/contribuer") ||
    pathname.startsWith("/profile/account/") ||
    pathname.startsWith("/profile/setting") ||
    pathname === "/veille" ||
    pathname.startsWith("/veille/") ||
    pathname === "/avis" ||
    pathname.startsWith("/avis/") ||
    pathname === "/formation" ||
    pathname.startsWith("/formation/") ||
    pathname === "/modules" ||
    pathname === "/masterclass" ||
    pathname.startsWith("/masterclass/") ||
    pathname === "/agenda" ||
    pathname.startsWith("/event/") ||
    pathname === "/library" ||
    pathname.startsWith("/library/") ||
    pathname === "/apprentissage" ||
    pathname.includes("/apprentissage") ||
    pathname === "/certificats" ||
    pathname === "/favorites" ||
    pathname === "/medicaments" ||
    pathname.startsWith("/medicaments/") ||
    pathname === "/pathologies" ||
    pathname.startsWith("/pathologies/")
  );
}

// ==============================|| MAIN LAYOUT ||============================== //

function MainLayoutInner() {
  const theme = useTheme();
  useSelector((state) => state.auth.user);
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery(theme.breakpoints.down("xl"));
  const downLG = useMediaQuery(theme.breakpoints.down("lg"));
  const { open: searchOpen } = useGlobalSearch();
  const [searchCovered, setSearchCovered] = useState(false);

  const { container, miniDrawer, menuOrientation } = useConfig();
  const { pathname } = useLocation();
  const mainRef = useRef(null);
  const isV2Shell = useIsV2Shell(pathname);

  const isHorizontal =
    menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (searchOpen) {
      setSearchCovered(true);
      if (mainRef.current) {
        mainRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return undefined;
    }
    const t = setTimeout(() => setSearchCovered(false), 420);
    return () => clearTimeout(t);
  }, [searchOpen]);

  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  if (menuMasterLoading) return <Loader />;

  return (
    <RouteGuard>
      <Box
        className={isV2Shell ? "dashboard-v2-shell" : undefined}
        sx={{
          display: "flex",
          width: "100%",
          minHeight: "100vh",
          bgcolor: isV2Shell ? "transparent" : "white",
          overflow: "hidden",
        }}
      >
        {!isV2Shell && <Header />}
        <Drawer />

        <Box
          ref={mainRef}
          component="main"
          className={
            isV2Shell
              ? `dashboard-v2-main${searchCovered ? " is-search-open" : ""}`
              : searchCovered
                ? "is-search-open"
                : undefined
          }
          sx={{
            width: { xs: "100%", lg: `calc(100% - ${DRAWER_SLOT_WIDTH}px)` },
            flexGrow: 1,
            flex: "1 1 auto",
            minWidth: 0,
            py: isV2Shell ? 0 : 5,
            px: isV2Shell ? { xs: 2, sm: 3 } : { md: 3 },
            height: "100vh",
            overflowY: searchCovered ? "hidden" : "auto",
            position: "relative",
            zIndex: 1,
            bgcolor: "transparent",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isV2Shell ? (
            <PageChrome />
          ) : (
            <Toolbar
              sx={{
                mt: isHorizontal ? 8 : "inherit",
                mb: isHorizontal ? 2 : "inherit",
                minHeight: `${HEADER_HEIGHT}px !important`,
                bgcolor: "transparent",
                background: "transparent",
                flexShrink: 0,
              }}
            />
          )}
          <Box
            className={`dashboard-content-stack${searchCovered ? " is-search-open" : ""}`}
            sx={{
              position: "relative",
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className={`dashboard-page-outlet${searchCovered ? " is-search-covered" : ""}`}
            >
              <Container
                maxWidth={container && !isV2Shell ? "xl" : false}
                sx={{
                  xs: 0,
                  ...(container && !isV2Shell && { px: { xs: 0, md: 2 } }),
                  position: "relative",
                  minHeight: isV2Shell ? "auto" : "calc(100vh - 110px)",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "transparent",
                  ...(isV2Shell && {
                    maxWidth: "100% !important",
                    px: "0 !important",
                    py: 0,
                  }),
                }}
              >
                <Outlet />
              </Container>
            </div>
            <GlobalSearch />
          </Box>
        </Box>
      </Box>
    </RouteGuard>
  );
}

export default function MainLayout() {
  return (
    <GlobalSearchProvider>
      <MainLayoutInner />
    </GlobalSearchProvider>
  );
}
